// DN detail «Солих / Оноох» (Ord_DeliveryModals 4): reassign the driver, vehicle and/or date
// (POST /ord/delivery-note/:id/reassign, DEL_CHANNEL_OVERRIDE). The BE moves the DN's staffId, sets the
// vehicle on the DN's open route (refused once the route has left) and writes the date as the DN's
// rescheduled delivery date.

import { useState } from "react"
import { Alert, Box, Button, Group, Modal, Select, SimpleGrid, Stack, Text, Textarea } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import dayjs from "dayjs"
import { nameOptionsFilter } from "@dehub/theme"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { DeliveryApi, VehicleApi } from "../../apis"
import { message } from "../../utils/message"
import { renderName } from "../../utils/render-name"
import { translateBackendError } from "../delivery/translate-error"
import { usePermission } from "../permission"

type Props = {
  opened: boolean
  onClose: () => void
  deliveryNoteId: string
  /** DN-… shown under the title. */
  refCode?: string | null
  currentStaffId?: string | null
  businessId: string
  onReassigned?: () => void
  previousExceptionId?: string | null
}

export function DnReassignDialog({ opened, onClose, deliveryNoteId, refCode, currentStaffId, businessId, onReassigned, previousExceptionId }: Props) {
  const { t } = useTranslation(["delivery", "common"])

  const [toDriverId, setToDriverId] = useState<string | null>(null)
  const [toVehicleId, setToVehicleId] = useState<string | null>(null)
  const [toDate, setToDate] = useState<string | null>(null)
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canAssign = usePermission("ORD_DN_ASSIGN").hasAccess
  const { data: staffList, isLoading: loadingStaff } = useSWR(
    opened && businessId && canAssign ? `/ord/delivery_note/staff_select:${businessId}` : null,
    () => DeliveryApi.staffSelect({ businessId }),
    { revalidateOnFocus: false },
  )

  const { data: vehicles, isLoading: loadingVehicles } = useSWR(
    opened ? "reassign.vehicles.active" : null,
    () => VehicleApi.list({ filter: { isActive: true }, offset: { limit: 100, skip: 0 } }),
    { revalidateOnFocus: false },
  )
  const vehicleOptions = (vehicles?.rows ?? []).map((v) => ({
    value: v.id,
    label: [v.plateNumber, [v.make, v.model].filter(Boolean).join(" ")].filter(Boolean).join(" "),
  }))

  const driverOptions = (staffList ?? []).map((s: any) => ({
    value: s.id,
    label: [renderName(s), s.phone ? ` (${s.phone})` : ""].join(""),
  }))

  const reset = () => {
    setToDriverId(null)
    setToVehicleId(null)
    setToDate(null)
    setReason("")
    setError(null)
  }

  const handleClose = () => {
    if (submitting) return
    reset()
    onClose()
  }

  const sameDriver = !!toDriverId && toDriverId === currentStaffId
  const hasChange = !!(toDriverId || toVehicleId || toDate)
  const isValid = hasChange && !sameDriver && reason.trim().length >= 3

  const onSubmit = async () => {
    if (!isValid) return
    setSubmitting(true)
    setError(null)
    try {
      await DeliveryApi.reassign(deliveryNoteId, {
        toDriverId,
        fromDriverId: toDriverId ? currentStaffId ?? null : null,
        toVehicleId,
        toScheduledDate: toDate,
        reason: reason.trim(),
        previousExceptionId: previousExceptionId ?? null,
      })
      message.success(t("common:messages.requestSuccess"))
      onReassigned?.()
      reset()
      onClose()
    } catch (err) {
      setError(translateBackendError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size={600}
      radius="lg"
      centered
      title={
        <Box>
          <Text fw={650} fz="var(--nw-fz-drawer)">
            {t("delivery.reassign.heading")}
          </Text>
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
            {t("delivery.reassign.subtitle", { code: refCode ?? "" })}
          </Text>
        </Box>
      }>
      <Stack gap="md">
        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <SimpleGrid cols={2} spacing="md">
          <Select
            label={t("delivery.reassign.driver")}
            placeholder={t("delivery.reassign.driverPlaceholder")}
            data={driverOptions}
            value={toDriverId}
            onChange={setToDriverId}
            searchable
            filter={nameOptionsFilter}
            clearable
            disabled={loadingStaff}
            error={sameDriver ? t("delivery.reassign.sameDriver") : undefined}
          />
          <Select
            label={t("delivery.reassign.vehicle")}
            placeholder={t("delivery.reassign.vehiclePlaceholder")}
            data={vehicleOptions}
            value={toVehicleId}
            onChange={setToVehicleId}
            searchable
            clearable
            disabled={loadingVehicles}
          />
        </SimpleGrid>

        <DatePickerInput
          label={t("delivery.reassign.date")}
          placeholder={t("delivery.reassign.datePlaceholder")}
          value={toDate}
          onChange={(v) => setToDate(v ? dayjs(v).format("YYYY-MM-DD") : null)}
          minDate={dayjs().startOf("day").toDate()}
          valueFormat="YYYY-MM-DD"
          clearable
        />

        <Textarea
          label={t("delivery.reassign.reason")}
          description={t("delivery.reassign.reasonHint")}
          placeholder={t("delivery.reassign.reasonPlaceholder")}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={3}
          autosize
          required
          maxLength={2000}
          error={reason.length > 0 && reason.trim().length < 3 ? t("delivery.reassign.reasonTooShort") : undefined}
        />
        <Text fz="var(--nw-fz-meta)" c="var(--nw-text-2)" ff="monospace" ta="right" mt={-8}>
          {Math.min(reason.trim().length, 3)} / 3
        </Text>

        <Stack gap={4} align="flex-end">
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose} disabled={submitting}>
              {t("common:actions.dismiss")}
            </Button>
            <Button color="dark" onClick={onSubmit} disabled={!isValid} loading={submitting}>
              {t("delivery.reassign.title")}
            </Button>
          </Group>
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
            {t("common:permission.required", { code: "DEL_CHANNEL_OVERRIDE" })}
          </Text>
        </Stack>
      </Stack>
    </Modal>
  )
}
